import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getCropColor } from '../utils/cropColors';
import CropLegend from './CropLegend';
// Set your Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

interface FieldWithCrop {
  field_id: string;
  fieldname: string;
  farm_id: string;
  acres: number | string; // Can be number or string from API
  geojson?: string;
  state: string;
  county: string;
  crop?: string;
  business: string;
  farm: string;
  planting_date?: string;
  harvest_date?: string;
  cover_crop_used?: string;
  yield?: number; // Total bushels
}

interface CroppedFieldsMapProps {
  fields: FieldWithCrop[];
  selectedFieldId?: string;
  mapStyle?: 'satellite' | 'street';
  onMapLoad?: (map: mapboxgl.Map) => void;
}

const CroppedFieldsMap: React.FC<CroppedFieldsMapProps> = ({
  fields,
  selectedFieldId,
  mapStyle = 'satellite',
  onMapLoad
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const popup = useRef<mapboxgl.Popup | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);

  // Get unique crops from the fields to show in legend
  const visibleCrops = Array.from(new Set(fields.map(field => field.crop).filter(Boolean) as string[]));

  // Crop color mapping
  const getCropColorForField = (cropName: string): string => {
    const normalizedCrop = cropName?.toLowerCase().replace(/\s+/g, '');
    return getCropColor(normalizedCrop);
  };

  // Create popup content
  const createPopupContent = (field: FieldWithCrop) => {
    const content = document.createElement('div');
    content.style.padding = '8px';
    content.style.fontSize = '12px';
    content.style.lineHeight = '1.4';
    
    // Safely handle acres - convert to number if it's a string
    const acresValue = typeof field.acres === 'string' ? parseFloat(field.acres) : field.acres;
    const acresDisplay = acresValue && !isNaN(acresValue) ? acresValue.toFixed(1) : 'N/A';
    
    // Calculate yield per acre if both yield and acres are available
    let yieldDisplay = '';
    if (field.yield && acresValue && !isNaN(acresValue) && acresValue > 0) {
      const yieldPerAcre = field.yield / acresValue;
      yieldDisplay = `Yield: ${yieldPerAcre.toFixed(1)} bu/ac`;
    } else if (field.yield) {
      yieldDisplay = `Total Yield: ${field.yield.toFixed(1)} bu`;
    }
    
    const lines = [
      `<strong>${field.business}</strong>`,
      `<strong>${field.farm}</strong>`,
      `<strong>${field.fieldname}</strong>`,
      `Acres: ${acresDisplay}`,
      field.crop ? `Crop: ${field.crop}` : 'No Crop',
      yieldDisplay,
      field.planting_date ? `Planting: ${new Date(field.planting_date).toLocaleDateString()}` : '',
      field.harvest_date ? `Harvest: ${new Date(field.harvest_date).toLocaleDateString()}` : '',
      field.cover_crop_used ? field.cover_crop_used : ''
    ].filter(line => line); // Remove empty lines
    
    content.innerHTML = lines.join('<br>');
    return content;
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle === 'satellite'
        ? 'mapbox://styles/mapbox/satellite-v9'
        : 'mapbox://styles/mapbox/streets-v11',
      center: [-93.79028, 42.00849], // Default to Iowa
      zoom: 10
    });

    // Initialize popup
    popup.current = new mapboxgl.Popup({
      offset: 25,
      closeButton: false,
      closeOnClick: false
    });

    map.current.on('load', () => {
      setIsMapLoaded(true);
      if (onMapLoad && map.current) {
        onMapLoad(map.current);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [mapStyle, onMapLoad]);

  // Load field polygons when map is ready and fields change
  useEffect(() => {

    if (!isMapLoaded || !map.current || !fields.length) return;

    // Remove existing field layers and sources
    if (map.current.getLayer('fields-fill')) {
      map.current.removeLayer('fields-fill');
    }
    if (map.current.getLayer('fields-stroke')) {
      map.current.removeLayer('fields-stroke');
    }
    if (map.current.getSource('fields')) {
      map.current.removeSource('fields');
    }

    // Create GeoJSON from fields with crop colors

    const fieldFeatures = fields
      .filter(field => field.geojson) // Only include fields with geojson
      .map(field => {
        try {
          const geojson = JSON.parse(field.geojson!); // Use non-null assertion since we filtered
          const cropColor = field.crop ? getCropColorForField(field.crop) : null;
          
          return {
            type: 'Feature' as const,
            geometry: geojson,
            properties: {
              field_id: field.field_id,
              fieldname: field.fieldname,
              crop: field.crop,
              cropColor: cropColor,
              acres: field.acres,
              farm: field.farm,
              business: field.business,
              planting_date: field.planting_date,
              harvest_date: field.harvest_date,
              cover_crop_used: field.cover_crop_used,
              yield: field.yield,
              isSelected: field.field_id === selectedFieldId
            }
          };
        } catch (error) {
          console.error('Error parsing geojson for field:', field.field_id, error);
          return null;
        }
      })
      .filter((feature): feature is NonNullable<typeof feature> => feature !== null); // Type-safe filter

    if (fieldFeatures.length === 0) return;

    // Add source
    map.current.addSource('fields', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: fieldFeatures
      }
    });

    // Add fill layer
    map.current.addLayer({
      id: 'fields-fill',
      type: 'fill',
      source: 'fields',
      paint: {
        'fill-color': [
          'coalesce', ['get', 'cropColor'], '#FFFFFF' 
        ],
        'fill-opacity':  0.4
      }
    });

    // Add stroke layer
    map.current.addLayer({
      id: 'fields-stroke',
      type: 'line',
      source: 'fields',
      paint: {
        'line-color': [
          'coalesce', ['get', 'cropColor'],'#FFFFFF' 
        ],
        'line-width': [
          'case',
          ['get', 'isSelected'],
          2,  // Width 2 for selected field
          1   // Width 1 for other fields
        ]
      }
    });

    // Track current hovered field to avoid unnecessary updates
    let currentHoveredFieldId: string | null = null;

    // Add hover effect with popup
    map.current.on('mouseenter', 'fields-fill', (e) => {
      map.current!.getCanvas().style.cursor = 'pointer';
      
      if (e.features && e.features[0] && popup.current) {
        const feature = e.features[0];
        const fieldId = feature.properties?.field_id;
        const field = fields.find(f => f.field_id === fieldId);
        
        if (field) {
          // Always remove existing popup first to ensure clean update
          if (popup.current.isOpen()) {
            popup.current.remove();
          }
          
          // Update current hovered field
          currentHoveredFieldId = fieldId;
          
          const popupContent = createPopupContent(field);
          popup.current
            .setLngLat(e.lngLat)
            .setDOMContent(popupContent)
            .addTo(map.current!);
        }
      }
    });

    // Update popup position as mouse moves within the polygon
    map.current.on('mousemove', 'fields-fill', (e) => {
      if (e.features && e.features[0] && popup.current && popup.current.isOpen()) {
        const feature = e.features[0];
        const fieldId = feature.properties?.field_id;
        
        // Only update position if we're still on the same field
        if (fieldId === currentHoveredFieldId) {
          popup.current.setLngLat(e.lngLat);
        } else {
          // Different field - update content and position
          currentHoveredFieldId = fieldId;
          const field = fields.find(f => f.field_id === fieldId);
          if (field) {
            if (popup.current.isOpen()) {
              popup.current.remove();
            }
            const popupContent = createPopupContent(field);
            popup.current
              .setLngLat(e.lngLat)
              .setDOMContent(popupContent)
              .addTo(map.current!);
          }
        }
      }
    });

    map.current.on('mouseleave', 'fields-fill', () => {
      map.current!.getCanvas().style.cursor = '';
      currentHoveredFieldId = null;
      if (popup.current && popup.current.isOpen()) {
        popup.current.remove();
      }
    });

    // Fit map to show selected field or all fields
    if (fieldFeatures.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();

      // If there's a selected field ID, only zoom to that field
      if (selectedFieldId) {
        const selectedFeature = fieldFeatures.find(f => f.properties.field_id === selectedFieldId);
        if (selectedFeature) {
          if (selectedFeature.geometry.type === 'Polygon') {
            selectedFeature.geometry.coordinates[0].forEach((coord: number[]) => {
              if (coord.length >= 2) {
                bounds.extend([coord[0], coord[1]] as [number, number]);
              }
            });
          } else if (selectedFeature.geometry.type === 'MultiPolygon') {
            selectedFeature.geometry.coordinates.forEach((polygon: number[][][]) => {
              polygon[0].forEach((coord: number[]) => {
                if (coord.length >= 2) {
                  bounds.extend([coord[0], coord[1]] as [number, number]);
                }
              });
            });
          }
          
          map.current!.fitBounds(bounds, {
            padding: 100,
            maxZoom: 18 // Closer zoom for individual field
          });
        }
      } else {
        // No specific field selected, fit to all fields
        fieldFeatures.forEach(feature => {
          if (feature.geometry.type === 'Polygon') {
            feature.geometry.coordinates[0].forEach((coord: number[]) => {
              if (coord.length >= 2) {
                bounds.extend([coord[0], coord[1]] as [number, number]);
              }
            });
          } else if (feature.geometry.type === 'MultiPolygon') {
            feature.geometry.coordinates.forEach((polygon: number[][][]) => {
              polygon[0].forEach((coord: number[]) => {
                if (coord.length >= 2) {
                  bounds.extend([coord[0], coord[1]] as [number, number]);
                }
              });
            });
          }
        });
        
        map.current!.fitBounds(bounds, {
          padding: 30,
          maxZoom: 15
        });
      }
    }

  }, [isMapLoaded, fields, selectedFieldId]);

  return (
    <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 435px)' }}>
      {/* Legend Toggle Button - Top Right */}
      <button
        onClick={() => setIsLegendOpen(!isLegendOpen)}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          padding: '8px 12px',
          fontSize: '0.9em',
          border: '1px solid #28a745',
          borderRadius: '4px',
          backgroundColor: '#28a745',
          color: 'white',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#218838';
          e.currentTarget.style.borderColor = '#218838';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = '#28a745';
          e.currentTarget.style.borderColor = '#28a745';
        }}
        title={isLegendOpen ? 'Hide Legend' : 'Show Legend'}
      >
        {isLegendOpen ? 'Hide Legend' : 'Show Legend'}
      </button>

      {/* Crop Legend - Left Side */}
      {isLegendOpen && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          maxHeight: 'calc(100vh - 455px)',
          overflowY: 'auto',
          maxWidth: '300px',
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '2px 2px 10px rgba(0,0,0,0.1)',
          padding: '15px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            borderBottom: '2px solid #007bff',
            paddingBottom: '10px'
          }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: '1.2em' }}>Legend</h3>
            <button
              onClick={() => setIsLegendOpen(false)}
              style={{
                background: '#f8f8f8',
                color: '#333',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#c82333';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ maxHeight: 'calc(100vh - 500px)', overflowY: 'auto' }}>
            {visibleCrops.length > 0 ? (
              visibleCrops.map((crop, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '4px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: getCropColor(crop),
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontSize: '0.9em', color: '#333' }}>{crop}</span>
                </div>
              ))
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
                No crops to display
              </div>
            )}
          </div>
        </div>
      )}

      {/* Map Container */}
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden'
        }} 
      />
    </div>
  );
};

export default CroppedFieldsMap;
